// Excel paste utilities for handling multi-cell paste operations

export const EXCEL_DELIMITERS = /[\t\n\r,;]+/;

const getUniqueValues = (values: string[]): string[] => {
  // Use Set to remove duplicates, then convert back to array
  return Array.from(new Set(values));
};

export const parsePastedContentAsUniqueValues = (content: string): string[] => {
  // Split by common Excel delimiters: tabs, newlines, commas, semicolons
  const values = content
    .split(EXCEL_DELIMITERS)
    .map(value => value.trim())
    .filter(value => value.length > 0);

  // Remove duplicates from pasted content
  return getUniqueValues(values);
};

export interface PasteResult {
  addedCount: number;
  totalValues: number;
  hasMultipleValues: boolean;
}

export const createUniquePasteHandler = (
  addSingle: (value: string) => boolean,
  addMultiple: (values: string[]) => number
) => {
  return (e: React.ClipboardEvent): PasteResult => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (!pastedText.trim()) {
      return { addedCount: 0, totalValues: 0, hasMultipleValues: false };
    }

    const values = parsePastedContentAsUniqueValues(pastedText);
    const hasMultipleValues = values.length > 1;

    if (hasMultipleValues) {
      const addedCount = addMultiple(values);
      return {
        addedCount,
        totalValues: values.length,
        hasMultipleValues: true,
      };
    } else if (values.length === 1) {
      const success = addSingle(values[0]);
      return {
        addedCount: success ? 1 : 0,
        totalValues: 1,
        hasMultipleValues: false,
      };
    }

    return { addedCount: 0, totalValues: 0, hasMultipleValues: false };
  };
};
